import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DollarSign, TrendingUp, Users, FileText, Share2, Award } from "lucide-react";
import { Database } from "@/integrations/supabase/types";

type ReferralPartner = Database["public"]["Tables"]["referral_partners"]["Row"];
type Referral = Database["public"]["Tables"]["referrals"]["Row"];

export default function ReferralPartnerPortal() {
  const [partner, setPartner] = useState<ReferralPartner | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const [newReferral, setNewReferral] = useState({
    institution_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    notes: "",
    lead_source: "email" as const,
  });

  useEffect(() => {
    loadPartnerData();
  }, []);

  const loadPartnerData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Not authenticated",
          description: "Please sign in to access the partner portal",
          variant: "destructive",
        });
        return;
      }

      const { data: partnerData, error: partnerError } = await supabase
        .from("referral_partners")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (partnerError) throw partnerError;
      setPartner(partnerData);

      const { data: referralsData, error: referralsError } = await supabase
        .from("referrals")
        .select("*")
        .eq("referral_partner_id", partnerData.id)
        .order("created_at", { ascending: false });

      if (referralsError) throw referralsError;
      setReferrals(referralsData || []);
    } catch (error: any) {
      toast({
        title: "Error loading data",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitReferral = async () => {
    if (!partner) return;
    
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("referrals").insert({
        referral_partner_id: partner.id,
        referral_code: partner.referral_code,
        referred_institution_name: newReferral.institution_name,
        referred_contact_name: newReferral.contact_name,
        referred_contact_email: newReferral.contact_email,
        referred_contact_phone: newReferral.contact_phone || null,
        notes: newReferral.notes || null,
        lead_source: newReferral.lead_source,
        status: "lead",
      });

      if (error) throw error;

      toast({
        title: "Referral Submitted!",
        description: "We'll contact the institution and keep you updated on progress.",
      });

      setNewReferral({
        institution_name: "",
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        notes: "",
        lead_source: "email",
      });

      loadPartnerData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      lead: "secondary",
      contacted: "secondary",
      demo_scheduled: "default",
      demo_completed: "default",
      proposal_sent: "default",
      negotiating: "default",
      closed_won: "default",
      closed_lost: "destructive",
    };
    return <Badge variant={variants[status] || "outline"}>{status.replace(/_/g, " ").toUpperCase()}</Badge>;
  };

  const formatCurrency = (amount: number | null) => {
    if (!amount) return "$0";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading partner data...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!partner) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-semibold mb-4">Not a Referral Partner</h2>
          <p className="text-muted-foreground mb-6">
            You need to be an approved referral partner to access this portal.
          </p>
          <Button onClick={() => window.location.href = "/referral-partner-apply"}>
            Apply to Become a Partner
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Referral Partner Portal</h1>
          <p className="text-muted-foreground">Welcome back, {partner.first_name}!</p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Referrals</p>
                <p className="text-2xl font-bold">{partner.total_referrals}</p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pipeline Value</p>
                <p className="text-2xl font-bold">{formatCurrency(partner.total_revenue_generated)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Commission Earned</p>
                <p className="text-2xl font-bold">{formatCurrency(partner.total_commission_earned)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-muted-foreground" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Commission Paid</p>
                <p className="text-2xl font-bold">{formatCurrency(partner.total_commission_paid)}</p>
              </div>
              <Award className="h-8 w-8 text-muted-foreground" />
            </div>
          </Card>
        </div>

        {/* Referral Code Card */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <Label>Your Referral Code</Label>
              <p className="text-2xl font-mono font-bold mt-1">{partner.referral_code}</p>
              <p className="text-sm text-muted-foreground mt-1">
                Share this code or use: athletenexus.com?ref={partner.referral_code}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(`athletenexus.com?ref=${partner.referral_code}`);
                toast({ title: "Copied to clipboard!" });
              }}
            >
              <Share2 className="h-4 w-4 mr-2" />
              Copy Link
            </Button>
          </div>
        </Card>

        <Tabs defaultValue="submit">
          <TabsList>
            <TabsTrigger value="submit">Submit Referral</TabsTrigger>
            <TabsTrigger value="pipeline">My Pipeline</TabsTrigger>
            <TabsTrigger value="resources">Resources</TabsTrigger>
          </TabsList>

          <TabsContent value="submit">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Submit New Referral</h2>
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Institution Name *</Label>
                    <Input
                      value={newReferral.institution_name}
                      onChange={(e) =>
                        setNewReferral({ ...newReferral, institution_name: e.target.value })
                      }
                      placeholder="University of..."
                    />
                  </div>
                  <div>
                    <Label>Contact Name *</Label>
                    <Input
                      value={newReferral.contact_name}
                      onChange={(e) =>
                        setNewReferral({ ...newReferral, contact_name: e.target.value })
                      }
                      placeholder="John Doe"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Email *</Label>
                    <Input
                      type="email"
                      value={newReferral.contact_email}
                      onChange={(e) =>
                        setNewReferral({ ...newReferral, contact_email: e.target.value })
                      }
                      placeholder="john@university.edu"
                    />
                  </div>
                  <div>
                    <Label>Contact Phone</Label>
                    <Input
                      type="tel"
                      value={newReferral.contact_phone}
                      onChange={(e) =>
                        setNewReferral({ ...newReferral, contact_phone: e.target.value })
                      }
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
                <div>
                  <Label>Lead Source</Label>
                  <Select
                    value={newReferral.lead_source}
                    onValueChange={(value: any) =>
                      setNewReferral({ ...newReferral, lead_source: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="linkedin">LinkedIn</SelectItem>
                      <SelectItem value="in_person">In Person</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={newReferral.notes}
                    onChange={(e) => setNewReferral({ ...newReferral, notes: e.target.value })}
                    placeholder="Any context about the opportunity, pain points, timeline, etc."
                    rows={4}
                  />
                </div>
                <Button onClick={handleSubmitReferral} disabled={isSubmitting} className="w-full">
                  {isSubmitting ? "Submitting..." : "Submit Referral"}
                </Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="pipeline">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Institution</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Commission</TableHead>
                    <TableHead>Submitted</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referrals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No referrals yet. Submit your first one to get started!
                      </TableCell>
                    </TableRow>
                  ) : (
                    referrals.map((referral) => (
                      <TableRow key={referral.id}>
                        <TableCell className="font-medium">
                          {referral.referred_institution_name}
                        </TableCell>
                        <TableCell>{referral.referred_contact_name}</TableCell>
                        <TableCell>{getStatusBadge(referral.status)}</TableCell>
                        <TableCell>{formatCurrency(referral.contract_value)}</TableCell>
                        <TableCell>
                          {referral.commission_paid ? (
                            <Badge variant="default">Paid: {formatCurrency(referral.commission_amount)}</Badge>
                          ) : (
                            formatCurrency(referral.commission_amount)
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(referral.created_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="resources">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Partner Resources</h2>
              <div className="space-y-4">
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="h-4 w-4 mr-2" />
                  Download Referral Kit PDF
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="h-4 w-4 mr-2" />
                  Email Templates & Scripts
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="h-4 w-4 mr-2" />
                  Objection Handling Guide
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="h-4 w-4 mr-2" />
                  Case Studies & Testimonials
                </Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
